import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect, useState } from "react";
import HomeLayout from "./pages/HomeLayout";
import StartPage from "./pages/StartPage.tsx";
import OnboardingPage from "./pages/OnboardingPage.tsx";
import NameInputPage from "./components/Onboarding/NameInputPage.tsx";
import CourseInfoPage from "./pages/CourseInfoPage";
import ChapterPathPage from "./pages/ChapterPathPage";
import ConstructionPage from "./pages/ConstructionPage";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import Lesson from "./pages/Lesson";
import TermsOfService from "./pages/Terms.tsx";
import Privacy from "./pages/Privacy.tsx";
import { Navigate } from "react-router-dom";
import SupportPage from "./pages/SupportPage.tsx";
import RedirectPage from "./pages/RedirectPage.tsx";
import PushControl from "./components/PushControl.tsx";
import IAPCard from "./components/AppleIAPCard.tsx";
import PremiumPage from "./pages/PremiumPage";
import StripeCheckout from "./pages/StripeCheckout.tsx";
import CheckoutComplete from "./pages/CheckoutComplete.tsx";
import ReviewPage from "./pages/ReviewPage.tsx";
import UpgradePage from "./pages/UpgradePage";
import { UpgradeProvider } from "./context/UpgradeContext";
import useVersionChecker from "./hooks/useVersionChecker";
import PlacementTestPage from "./pages/PlacementTestPage.tsx";
import ErrorPage from "./pages/ErrorPage.tsx";
import { cleanupLocalStorageWeekly } from "./lib/cleanup.ts";
import parrotdemo from "./assets/suset-onboarding-720p.mp4";
import iphone from "./assets/iphone.png";
import { fetchAndCacheAsset } from "@/lib/cache";

const AppHomePath = "/home";

function App() {
  const queryParams = new URLSearchParams(location.search);
  const backUrl = queryParams.get("back");

  const [parrotDemoUrl, setParrotDemoUrl] = useState<string>(parrotdemo);
  const [iphoneUrl, setIphoneUrl] = useState<string>(iphone);

  // Initialize version checking
  useVersionChecker();

  useEffect(() => {
    const fetchAssets = async () => {
      cleanupLocalStorageWeekly(/^lesson_\d+_activities$/);

      const parrotDemoBlob = await fetchAndCacheAsset(parrotdemo);
      const iphoneBlob = await fetchAndCacheAsset(iphone);
      if (!parrotDemoBlob || !iphoneBlob) {
        console.warn("Failed to prefetch assets");
        return;
      }
      const parrotDemoUrl = URL.createObjectURL(parrotDemoBlob);
      const iphoneUrl = URL.createObjectURL(iphoneBlob);
      setParrotDemoUrl(parrotDemoUrl);
      setIphoneUrl(iphoneUrl);
    };
    fetchAssets();
  }, []);

  return (
    <UpgradeProvider>
      <div className="App max-w-[100vw]">
        <Router basename="/app">
          <Routes>
            <Route
              path="/"
              element={backUrl ? <Navigate to={backUrl} /> : <StartPage />}
            />
            <Route path="/redirect" element={<RedirectPage />} />
            <Route path="/terms/*" element={<TermsOfService />} />
            <Route path="/privacy/*" element={<Privacy />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/placement" element={<PlacementTestPage />} />
            <Route path="/premium/*" element={<PremiumPage />} />
            <Route path="/checkout/*" element={<StripeCheckout />} />
            <Route path="/complete/*" element={<CheckoutComplete />} />
            <Route path="/iap" element={<IAPCard />} />
            <Route path="/push" element={<PushControl />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route
              path="/onboarding/*"
              element={
                <OnboardingPage
                  assetSources={{
                    parrotDemo: parrotDemoUrl,
                    iphone: iphoneUrl,
                  }}
                />
              }
            />
            <Route path="/name" element={<NameInputPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/error" element={<ErrorPage />} />
            <Route path={AppHomePath + "/*"} element={<HomeLayout />}></Route>
            <Route path="/info/:chapterId" element={<CourseInfoPage />} />
            <Route path="/chapter/:chapterId" element={<ChapterPathPage />} />
            <Route path="/construction" element={<ConstructionPage />} />
            <Route
              path="/chapter/:chapterId/lesson/:lessonId/*"
              element={<Lesson />}
            />
          </Routes>
        </Router>
      </div>
    </UpgradeProvider>
  );
}

export default App;
